import { doc, setDoc } from 'firebase/firestore'
import { db } from './firebase'

/* ============================================================================
   Geocoding.

   The Mac uses MapKit, which has no keyless web equivalent, so this uses
   OpenStreetMap's Nominatim — free and no API key, which is what lets a map
   ship today rather than after someone provisions billing.

   Nominatim asks for low volume, so every result is cached straight back onto
   the show document. A venue is geocoded once, ever, across all three clients:
   the second person to open that show reads coordinates, not the network.
   ============================================================================ */

export interface Coordinates {
  lat: number
  lon: number
}

const ENDPOINT = 'https://nominatim.openstreetmap.org/search'

/** In-memory cache, so re-opening a show in one session doesn't re-request. */
const session = new Map<string, Coordinates | null>()

export async function geocodeVenue(query: string): Promise<Coordinates | null> {
  const key = query.trim().toLowerCase()
  if (!key) return null
  if (session.has(key)) return session.get(key) ?? null

  try {
    const url = `${ENDPOINT}?format=jsonv2&limit=1&q=${encodeURIComponent(query)}`
    const response = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error(String(response.status))

    const rows = (await response.json()) as Array<{ lat: string; lon: string }>
    const first = rows[0]
    const result = first ? { lat: Number(first.lat), lon: Number(first.lon) } : null
    session.set(key, result)
    return result
  } catch (error) {
    console.warn('[geocode] failed', error)
    session.set(key, null)
    return null
  }
}

/**
 * Locates a venue, trying the most specific query first.
 *
 * A single "venue, address, city, country" string found six venues out of
 * seventy-one. Nominatim indexes addresses and places, not what a promoter
 * calls a room — "Rhythm and Vines Music Festival, Gisborne" matches nothing
 * while "Gisborne, New Zealand" matches immediately.
 *
 * So it steps down: exact address, then venue and city, then the city alone. A
 * city-level coordinate is not the venue's door, but it is right to within a
 * few kilometres, which is all a map pin needs to be useful. The alternative
 * is a blank panel.
 *
 * This is the same chain `repairs.ts` runs server-side. Having it here too is
 * what makes the map work for a show that was imported rather than typed in,
 * without waiting for a repair pass.
 */
export async function locateVenue(parts: {
  venueName?: string | null
  venueAddress?: string | null
  city?: string | null
  country?: string | null
}): Promise<Coordinates | null> {
  const venue = (parts.venueName ?? '').trim()
  const address = (parts.venueAddress ?? '').trim()
  const city = (parts.city ?? '').trim()
  const country = (parts.country ?? '').trim()

  const attempts = [
    address ? [address, city, country].filter(Boolean).join(', ') : '',
    venue && city ? [venue, city, country].filter(Boolean).join(', ') : '',
    [city, country].filter(Boolean).join(', '),
  ].filter((query) => query.length > 0)

  for (const query of attempts) {
    const found = await geocodeVenue(query)
    if (found) return found
  }

  return null
}

/**
 * Writes the coordinates back onto the show.
 *
 * Additive fields — Swift's decoder ignores keys it doesn't know, so the native
 * apps are unaffected. Failure is swallowed on purpose: a viewer has no write
 * access, and a map that renders shouldn't produce an error because the cache
 * couldn't be saved.
 */
export async function cacheShowCoordinates(
  showId: string,
  coordinates: Coordinates,
): Promise<void> {
  try {
    await setDoc(
      doc(db, 'shows', showId),
      { venueLat: coordinates.lat, venueLon: coordinates.lon },
      { merge: true },
    )
  } catch {
    /* read-only user, or offline — the session cache still holds it */
  }
}

/**
 * A bounding box around a point, for the OSM embed.
 *
 * `span` is in degrees; 0.008 is roughly a few streets, which is the useful
 * zoom for "where is the load-in door" rather than "which city".
 */
export function boundingBox({ lat, lon }: Coordinates, span = 0.008): string {
  return [lon - span, lat - span / 2, lon + span, lat + span / 2]
    .map((value) => value.toFixed(6))
    .join(',')
}

/* ------------------------------------------------------------- timezones ---- */

/**
 * The timezone a set of coordinates actually sits in.
 *
 * Open-Meteo answers this as a side effect of a forecast request: free, no
 * key, no attribution requirement. One field of the response is all we want.
 *
 * This exists because the show form used to seed `timezone` from
 * `browserTimeZone()` — the person's own zone. A tour manager in Auckland
 * adding a Berkeley date got `Pacific/Auckland` written onto the show, and
 * every load-in, set time and day sheet after it was twenty-one hours wrong.
 * A venue's timezone is a property of the venue, never of whoever typed it in.
 */
const zoneCache = new Map<string, string | null>()

export async function lookupTimeZone(lat: number, lon: number): Promise<string | null> {
  const key = `${lat.toFixed(3)},${lon.toFixed(3)}`
  if (zoneCache.has(key)) return zoneCache.get(key) ?? null

  try {
    const url =
      'https://api.open-meteo.com/v1/forecast' +
      `?latitude=${lat}&longitude=${lon}&timezone=auto&forecast_days=1`
    const response = await fetch(url)
    if (!response.ok) throw new Error(String(response.status))

    const data = (await response.json()) as { timezone?: string }
    const zone = typeof data.timezone === 'string' && data.timezone.includes('/')
      ? data.timezone
      : null
    zoneCache.set(key, zone)
    return zone
  } catch (error) {
    console.warn('[geocode] timezone lookup failed', error)
    zoneCache.set(key, null)
    return null
  }
}

/**
 * Coordinates and timezone for a place, in one call.
 *
 * Returns nulls rather than throwing: a show that can't be located is still a
 * show, and refusing to save it would be worse than saving it without a map.
 */
export async function locate(
  query: string,
): Promise<{ lat: number | null; lon: number | null; timezone: string | null }> {
  const found = await geocodeVenue(query)
  if (!found) return { lat: null, lon: null, timezone: null }
  const timezone = await lookupTimeZone(found.lat, found.lon)
  return { lat: found.lat, lon: found.lon, timezone }
}
